<script lang="ts" setup>
import AppState, { getCalendarsInLocation, resetCalendarEditValidationData } from '@/states/app'
import CalendarShareModal from '@/components/Settings/CalendarShareModal.vue'
import { SHARE_SOURCE, type ICalendarShare } from '@/types'
import calendarScheduleState from '@/states/calendarScheduleState'
import { CalendarType } from '@/models/calendar'
import { removeCalendarIdFromQueryParams, toggleAdvancedView } from '@/utils/commonModalFunctions'
import { mountCalendarMeetings, unmountCalendarMeetings } from '@/utils/remoteAppIntegration'
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AdvancedCalendarSetup from './AdvancedCalendarSetup.vue'
import ArchivedServices from './ArchivedServices.vue'
import NewCalendarSettings from './NewCalendarSettings.vue'
import ServiceMenuSetup from './ServiceMenuSetup.vue'

const MEETINGS_MOUNT_ID = 'CalendarMeetingsShellMount'

// Return to the calendars list (mirrors legacy AdvancedCalendarSetup.handleCancel). The meetings MFA
// has no access to the host AppState, so it emits `close-meetings` and the host performs the
// navigation: clear the editing draft, drop the calendar id from the URL, and collapse the
// advanced view so the list renders again.
const closeMeetings = (): void => {
  AppState.draftCalendarData = null
  AppState.editingCalendar = null
  AppState.selectedCalendarId = ''
  AppState.selectedCalendarType = null
  AppState.isNewCalendar = false
  removeCalendarIdFromQueryParams()
  if (AppState.showAdvancedCalendarSettings) {
    toggleAdvancedView()
  }
  void getCalendarsInLocation(true)
  calendarScheduleState.clearState()
}

/**
 * Share details the meetings MFA sends when its header Share button is pressed. The modal is a host
 * component, so the MFA cannot open it directly and hands over what the modal renders.
 */
interface ShareCalendarRequest {
  calendarId?: string
  name?: string
  slug?: string
  calendarType?: string
  slotDuration?: string
  slotDurationUnit?: string
  widgetType?: string
  /** Neo embeds key their iframe id off this, falling back to the calendar id. */
  providerId?: string
  /** The dialog opens before the save finishes and shows its own banner while it runs. */
  isSaving?: boolean
}

const { t } = useI18n()

const showShareCalendarModal = ref(false)
const shareCalendarData = ref<ICalendarShare | null>(null)

const openShareCalendar = (request: ShareCalendarRequest): void => {
  // A share sends two events: one on click, one once the save lands. The second only refreshes an
  // already-open dialog, so it must not reopen one the user has closed.
  if (!request?.isSaving && !showShareCalendarModal.value) {
    return
  }
  shareCalendarData.value = {
    id: request?.calendarId,
    slug: request?.slug,
    name: request?.name,
    calendarType: request?.calendarType,
    slotDuration: request?.slotDuration,
    slotDurationUnit: request?.slotDurationUnit,
    widgetType: request?.widgetType,
    providerId: request?.providerId,
    source: SHARE_SOURCE.CALENDAR,
    schedulePath: 'bookings',
    permanentPath: 'booking',
    otlPath: 'otl',
    showOneTimeLink: true,
    isSaving: Boolean(request?.isSaving),
    loadingMessage: request?.isSaving ? t('calendar_advanced.general.saving_changes') : '',
  }
  showShareCalendarModal.value = true
}

const closeShareCalendar = (): void => {
  showShareCalendarModal.value = false
  shareCalendarData.value = null
}

type MediaHandler = (...args: unknown[]) => void
const mediaHandlers: Record<string, MediaHandler[]> = {}

// Handlers deregister themselves from their own unmount hooks, so the list can mutate mid-dispatch.
const dispatchLocal = (event: string, ...args: unknown[]): void => {
  ;[...(mediaHandlers[event] ?? [])].forEach(handler => handler(...args))
}

// Bridges the meetings MFA's emitter to the host media library across the legacy
// Postmate boundary: forward open requests to the parent, surface the picked URL back.
const mediaEmitter = {
  on(event: string, handler: MediaHandler) {
    ;(mediaHandlers[event] ??= []).push(handler)
  },
  off(event: string, handler?: MediaHandler) {
    mediaHandlers[event] = (mediaHandlers[event] ?? []).filter(h => h !== handler)
  },
  emit(event: string, ...args: unknown[]) {
    if (event === 'media-library-open') {
      AppState.isMediaLibraryOpen = true
      const request = (args[0] && typeof args[0] === 'object' ? args[0] : {}) as Record<
        string,
        unknown
      >
      AppState.parentApp?.then((parent: any) =>
        parent.emit('media-library-open', {
          apiKey: '',
          tokenId: AppState.tokenId,
          companyId: AppState.companyId,
          altId: AppState.locationId,
          altType: 'location',
          hideHeader: true,
          allowedExtensions: ['image'],
          maxUploadSize: 50000000,
          category: 'calendars',
          subCategory: 'calendar_settings',
          ...request,
        })
      )
      return
    }
    if (event === 'close-meetings') {
      closeMeetings()
      return
    }
    if (event === 'share-calendar') {
      openShareCalendar(args[0] as ShareCalendarRequest)
      return
    }
    if (event === 'closeMediaModal') {
      // Echo back into the meetings as well as forwarding: the media fields disarm their siblings with
      // this event, and the Postmate parent never emits it. Without the echo, a field left armed by a
      // dismissed media library would also consume the next selection meant for a different field.
      dispatchLocal(event, ...args)
    }
    // Forward the meetings's navigation events (open-email-builder, edit-email-template,
    // go-to-whatsapp-settings, …) to the Postmate parent, as the legacy components did directly.
    AppState.parentApp?.then((parent: any) => parent.emit(event, ...args))
  },
}

// The CRM owns the Text Snippet modal; App.vue turns its close callback into this flag. Relay it so
// the meetings can refetch the templates the modal just wrote.
const stopSmsTemplateWatch = watch(
  () => AppState.fetchSmsTemplates,
  pending => {
    if (!pending) {
      return
    }
    AppState.fetchSmsTemplates = false
    dispatchLocal('sms-templates-updated')
  }
)

// Editor for the opened calendar: true = meetings MFA, false = legacy page, null = not chosen yet.
const useMeetingsEditor = ref<boolean | null>(null)

const stopMediaWatch = watch(
  () => AppState.mediaFileURI,
  uri => {
    if (!uri) {
      return
    }
    // This page stays mounted for both the meetings MFA and the legacy editor. Only the MFA
    // consumes mediaSelected; if we clear the URI while the legacy pages are showing, their
    // watchers never see the pick (logo / cover image look like media storage is broken).
    if (useMeetingsEditor.value !== true) {
      return
    }
    // Clear before fan-out, as the legacy MeetingDetailsPage/ExtrasPage watchers did: the value has to
    // change for this watch to fire again, so re-picking the same asset is otherwise dropped silently.
    AppState.mediaFileURI = ''
    dispatchLocal('mediaSelected', { url: uri })
  }
)

// App.vue turns the CRM's whatsappTemplateCreated callback into this flag; relay it so the meetings
// refresh their template list and select the saved template. Legacy pages consume the flag themselves.
const stopWhatsAppTemplateWatch = watch(
  () => AppState.whatsappNotificationTemplate.isUpdated,
  updated => {
    if (!updated || useMeetingsEditor.value !== true) {
      return
    }
    const { templateId } = AppState.whatsappNotificationTemplate
    AppState.whatsappNotificationTemplate.isUpdated = false
    AppState.whatsappNotificationTemplate.templateId = ''
    dispatchLocal('whatsapp-template-saved', { templateId })
  }
)

onBeforeUnmount(() => {
  stopMediaWatch()
  stopSmsTemplateWatch()
  stopWhatsAppTemplateWatch()
  // The watch below normally tears the meetings down when the advanced view closes, but this component
  // can be destroyed while that view is still open (route change, permission/feature-flag flip). Without
  // this the remote instance stays alive against DOM Vue has already removed.
  unmountCalendarMeetings(MEETINGS_MOUNT_ID)
})

watch(
  () => AppState.queryParams,
  () => {
    const calendarId = AppState.queryParams?.calendarId ?? AppState.queryParams?.calendar_id
    if (calendarId && typeof calendarId === 'string' && calendarId.trim()) {
      AppState.selectedCalendarId = calendarId.trim()
      resetCalendarEditValidationData()
      AppState.showAdvancedCalendarSettings = true
    } else {
      AppState.selectedCalendarId = ''
      AppState.showAdvancedCalendarSettings = false
    }
  },
  { immediate: true }
)

let editorOpenToken = 0

const findCalendarType = (calendarId: string): string | undefined =>
  AppState.globalCalendars?.calendars?.find(calendar => calendar.id === calendarId)?.calendarType

// Service calendars stay on the legacy editor even with the meetings MFA on.
const isServiceCalendarOpened = async (): Promise<boolean> => {
  const calendarId = AppState.selectedCalendarId as string
  if (!calendarId) {
    return AppState.draftCalendarData?.calendarType === CalendarType.SERVICE
  }
  // A deep link or a fresh duplicate can open a calendar the cached list does not hold yet.
  if (!findCalendarType(calendarId)) {
    await getCalendarsInLocation(true)
  }
  return findCalendarType(calendarId) === CalendarType.SERVICE
}

watch(
  () => AppState.showAdvancedCalendarSettings,
  async show => {
    const token = ++editorOpenToken
    if (!show) {
      useMeetingsEditor.value = null
      unmountCalendarMeetings(MEETINGS_MOUNT_ID)
      return
    }
    // Gate off: the template renders the legacy page, so there is no mount container.
    if (!AppState.isMergeCalendarTypeSetupEnabled) {
      useMeetingsEditor.value = false
      return
    }
    const isServiceCalendar = await isServiceCalendarOpened()
    // The advanced view can be closed or reopened while the calendar type resolves.
    if (token !== editorOpenToken || !AppState.showAdvancedCalendarSettings) {
      return
    }
    useMeetingsEditor.value = !isServiceCalendar
    if (isServiceCalendar) {
      return
    }
    await nextTick()
    // The advanced view can be closed again during the tick; skip a mount that is already stale.
    if (!AppState.showAdvancedCalendarSettings) {
      return
    }
    try {
      await mountCalendarMeetings(MEETINGS_MOUNT_ID, {
        locale: AppState.locale,
        calendarId: AppState.selectedCalendarId,
        locationId: AppState.locationId,
        tokenId: AppState.tokenId,
        authApiKey: AppState.authApiKey,
        companyId: AppState.companyId,
        couponEnabled: AppState.isCouponEnabled,
        smsEnabled: AppState.isSmsNotificationsEnabled,
        whatsappEnabled: AppState.isWhatsappNotificationsEnabled,
        loggedInUserInfo: AppState.user,
        teamMembers: ((AppState.globalUsers as any)?.users ?? []).map((user: any) => ({
          userId: user.id,
          name: user.name,
          profilePhoto: user.profilePhoto,
        })),
        mode: AppState.selectedCalendarId ? 'edit' : 'create',
        // Create seed from the "+ New Calendar" type picker; null = "Start from blank". Ignored on edit.
        calendarType: AppState.selectedCalendarType,
        emitter: mediaEmitter,
      })
    } catch (error) {
      // Remote unreachable / missing export: surface it instead of leaving an unhandled rejection and
      // an unexplained empty container.
      console.error('Calendar app: failed to mount the calendar meetings remote', error)
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="calendarSettingsMeetingsContainer">
    <div v-if="AppState.showAdvancedCalendarSettings">
      <div v-if="useMeetingsEditor" :id="MEETINGS_MOUNT_ID" />
      <AdvancedCalendarSetup v-else-if="useMeetingsEditor === false" />
    </div>
    <div v-else-if="AppState.showAdvancedServiceMenuSettings">
      <ServiceMenuSetup />
    </div>
    <div v-else>
      <ArchivedServices v-if="AppState.selectedSettingsTab === 'archived'" />
      <NewCalendarSettings v-else />
    </div>

    <!-- The same modal the calendars list and legacy setup page use. -->
    <CalendarShareModal
      v-if="showShareCalendarModal && shareCalendarData"
      :show-modal="showShareCalendarModal"
      :show="showShareCalendarModal"
      :data="shareCalendarData"
      @close-modal="closeShareCalendar"
    />
  </div>
</template>

<style scoped>
.calendarSettingsMeetingsContainer {
  font-family: 'Inter' !important;
}
</style>
